import { useState, useEffect } from 'react';
import { resolvePublicAsset } from '../utils/assets';

// Type for release notes entry
export interface ReleaseNotesEntry {
  [key: string]: unknown;
}

// Type for release notes hook return value
export interface UseReleaseNotesResult {
  releases: ReleaseNotesEntry[];
  loading: boolean;
  error: string | null;
}

export function useReleaseNotes(): UseReleaseNotesResult {
  const [releases, setReleases] = useState<ReleaseNotesEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Get the appropriate fetch function
    let fetchFn: typeof fetch | null = null;

    // Try different ways to get fetch
    if (typeof fetch !== 'undefined') {
      fetchFn = fetch;
    } else if (typeof globalThis !== 'undefined' && globalThis.fetch) {
      fetchFn = globalThis.fetch;
    } else if (typeof global !== 'undefined' && global.fetch) {
      fetchFn = global.fetch;
    } else if (typeof window !== 'undefined' && window.fetch) {
      fetchFn = window.fetch;
    }

    // If no fetch function is available, skip fetching
    if (!fetchFn || typeof fetchFn !== 'function') {
      setLoading(false);
      return;
    }

    // Fetch release notes data
    const releaseNotesUrl = resolvePublicAsset('release-notes.json');

    // Store the fetch function to prevent it from becoming undefined
    const safeFetch = fetchFn;

    try {
      const fetchPromise = safeFetch(releaseNotesUrl);

      if (!fetchPromise || typeof fetchPromise.then !== 'function') {
        setLoading(false);
        return;
      }

      fetchPromise
        .then(res => {
          if (!res.ok) {
            throw new Error(`Failed to fetch release notes: ${res.status}`);
          }
          return res.json();
        })
        .then(data => {
          // Ensure we always have an array to prevent mapping errors
          setReleases(Array.isArray(data) ? data : []);
        })
        .catch(err => {
          // Only log non-SyntaxError issues to reduce noise
          if (!(err instanceof SyntaxError)) {
            console.error(`Error loading release notes from ${releaseNotesUrl}:`, err);
          }
          setError(err.message);
        })
        .finally(() => {
          setLoading(false);
        });
    } catch (error) {
      console.error('Error during fetch setup:', error);
      setLoading(false);
    }
  }, []); // Empty dependency array ensures this runs only once

  return {
    releases,
    loading,
    error,
  };
}
